// src/components/ProdMgrs/components/ProdMgrsQuotaBreakdownRow.jsx
// =============================================================================
// BLOCK PMGR.QBR.0 — ProdMgrs QuotaBreakdownRow (safe DECIMAL formatting)
// Purpose:
//  - Clone behavior of shared QuotaBreakdownRow WITHOUT modifying shared code.
// Problem:
//  - Postgres DECIMAL/NUMERIC often arrives as string -> ".toFixed()" throws.
// Fix:
//  - Coerce numeric-ish values safely before formatting.
// =============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

// =============================================================================
// BLOCK PMGR.QBR.1 — Numeric helpers
// =============================================================================
function toNum(v) {
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : null;
  }
  if (typeof v === 'string') {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function fmtFixed(v, digits) {
  const n = toNum(v);
  return n === null ? '' : n.toFixed(digits);
}

// =============================================================================
// BLOCK PMGR.QBR.2 — Row component (ProdMgrs display columns)
// Notes:
//  - MUST match ProdMgrsQuotaBreakdownHeader exactly.
//  - PCI + Goal/PCI removed from ProdMgrs display.
//  - ExRep removed entirely.
// =============================================================================
function ProdMgrsQuotaBreakdownRow({ quota }) {
  const totalSamplesUsedAbs = React.useMemo(() => {
    const n = toNum(quota.TotalSamplesUsed);
    return n === null ? null : Math.abs(n);
  }, [quota.TotalSamplesUsed]);

  return (
    <TableRow hover>
      <TableCell>{quota.CATEGORY ?? ''}</TableCell>

      {/* Base */}
      <TableCell align="right">{fmtFixed(quota.Base, 2)}</TableCell>

      {/* Total Growth (if backend provides it; otherwise blank) */}
      <TableCell align="right">{fmtFixed(quota.TotalGrowth, 2)}</TableCell>

      {/* Tier 2 Start (moBase) */}
      <TableCell align="right">{fmtFixed(quota.moBase, 2)}</TableCell>

      {/* Tier 3 Start (moGoal) */}
      <TableCell align="right">{fmtFixed(quota.moGoal, 2)}</TableCell>

      {/* Rep Multi */}
      <TableCell align="right">{fmtFixed(quota.Rep_Mult, 2)}</TableCell>

      {/* Sample Rate */}
      <TableCell align="right">{fmtFixed(quota.D_Samp, 3)}</TableCell>

      {/* Total Samples Allotted */}
      <TableCell align="right">{fmtFixed(quota.TotalSamples, 2)}</TableCell>

      {/* Total Samples Used (abs) */}
      <TableCell align="right">
        {totalSamplesUsedAbs === null ? '' : totalSamplesUsedAbs.toFixed(2)}
      </TableCell>

      {/* Yearly Quota */}
      <TableCell align="right">{fmtFixed(quota.YearlyQuota, 2)}</TableCell>

      {/* EOY Bonus */}
      <TableCell align="right">{fmtFixed(quota.EOYB, 2)}</TableCell>
    </TableRow>
  );
}

ProdMgrsQuotaBreakdownRow.propTypes = {
  quota: PropTypes.shape({
    CATEGORY: PropTypes.string,

    Base: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    // Optional fields (depending on your API normalization)
    TotalGrowth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    moBase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    moGoal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TotalSamples: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TotalSamplesUsed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    YearlyQuota: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    Rep_Mult: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    D_Samp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    EOYB: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

export default React.memo(ProdMgrsQuotaBreakdownRow);
